import React, { useEffect, useState } from 'react';
import { Modal, Select, Input, Button } from 'antd';
import axios from 'axios';
import { API_BASE_URL } from '../config/apiconfig';

const { Option } = Select;
const { TextArea } = Input;

// status -> document category + default email title used by backend
const statusCategoryMap = {
  Interviews: 'call_for_interview',
  Scheduled: 'call_for_interview',
  Offer: 'offer_letter',
  Hired: 'offer_letter',
  Rejected: 'rejection',
  'Screening Disqualified': 'rejection',
};

export default function StatusChangeModal({
  visible,
  onCancel,
  applicationId,
  recruiterId,
  status,
  onSubmitted,
}) {
  const [docTemplates, setDocTemplates] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [documentTemplateId, setDocumentTemplateId] = useState(null);
  const [emailTemplateId, setEmailTemplateId] = useState(null);
  // Modes to simplify UX
  const [emailMode, setEmailMode] = useState('template'); // 'template' | 'custom'
  const [docMode, setDocMode] = useState('template');     // 'template' | 'none' | 'custom'
  const [editDoc, setEditDoc] = useState(false);          // show advanced editor for PDF
  const [editEmail, setEditEmail] = useState(false);      // show advanced editor for email
  const [overrideDocumentHtml, setOverrideDocumentHtml] = useState('');
  const [overrideEmailSubject, setOverrideEmailSubject] = useState('');
  const [overrideEmailBody, setOverrideEmailBody] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible) return;
    const category = statusCategoryMap[status] || undefined;
    (async () => {
      try {
        if (category) {
          const resDocs = await axios.get(`${API_BASE_URL}/api/templates/documents?created_by=${recruiterId}&category=${category}`);
          setDocTemplates(resDocs.data || []);
          // Preselect first doc template if exists
          if ((resDocs.data || []).length > 0) {
            setDocumentTemplateId(resDocs.data[0].id);
          } else {
            setDocMode('none');
          }
        } else {
          setDocTemplates([]);
          setDocMode('none');
        }
        const resEmails = await axios.get(`${API_BASE_URL}/api/templates/emails?created_by=${recruiterId}`);
        setEmailTemplates(resEmails.data || []);
        if ((resEmails.data || []).length > 0) {
          setEmailTemplateId(resEmails.data[0].id);
        } else {
          setEmailMode('custom');
        }
      } catch (e) {
        // fail silently
      }
    })();
    setEditDoc(false);
    setEditEmail(false);
    setOverrideDocumentHtml('');
    setOverrideEmailSubject('');
    setOverrideEmailBody('');
  }, [visible, status, recruiterId]);

  const submit = async () => {
    setLoading(true);
    try {
      // Build payload based on modes
      const payload = {
        status,
      };
      // Document handling
      if (docMode === 'template' && documentTemplateId) {
        payload.document_template_id = documentTemplateId;
        if (editDoc && overrideDocumentHtml) payload.override_document_html = overrideDocumentHtml;
      } else if (docMode === 'custom' && overrideDocumentHtml.trim()) {
        payload.override_document_html = overrideDocumentHtml;
      }
      // Email handling
      if (emailMode === 'template' && emailTemplateId) {
        payload.email_template_id = emailTemplateId;
        if (editEmail) {
          if (overrideEmailSubject.trim()) payload.override_email_subject = overrideEmailSubject;
          if (overrideEmailBody.trim()) payload.override_email_body = overrideEmailBody;
        }
      } else if (emailMode === 'custom') {
        payload.override_email_subject = overrideEmailSubject.trim() || 'Update regarding your application';
        payload.override_email_body = overrideEmailBody.trim() || `<p>Hello,</p><p>Your application status has been updated to ${status}.</p>`;
      }

      await axios.put(`${API_BASE_URL}/api/applications/${applicationId}/status`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      onSubmitted?.();
    } catch (e) {
      // optionally display toast in parent
      onSubmitted?.(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={`Confirm status change: ${status}`}
      open={visible}
      onCancel={() => onCancel?.()}
      footer={[
        <Button key="cancel" onClick={() => onCancel?.()}>Cancel</Button>,
        <Button key="submit" type="primary" loading={loading} onClick={submit}>Update Status & Send Email</Button>,
      ]}
      width={720}
    >
      <div className="space-y-6">
        {/* Document (PDF) Section */}
        <div>
          <label className="block mb-2 font-medium">PDF attachment</label>
          <div className="flex items-center gap-4 mb-3">
            <label className="flex items-center gap-2">
              <input type="radio" name="docMode" value="template" checked={docMode === 'template'} onChange={() => setDocMode('template')} />
              <span>Attach PDF from template</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" name="docMode" value="none" checked={docMode === 'none'} onChange={() => setDocMode('none')} />
              <span>No PDF</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" name="docMode" value="custom" checked={docMode === 'custom'} onChange={() => setDocMode('custom')} />
              <span>Custom PDF body</span>
            </label>
          </div>

          {docMode === 'template' && (
            <>
              <Select
                className="w-full"
                placeholder="Select document template"
                value={documentTemplateId}
                onChange={v => {
                  setDocumentTemplateId(v);
                  if (editDoc) {
                    const tpl = docTemplates.find(d => d.id === v);
                    setOverrideDocumentHtml(tpl?.body_html || '');
                  }
                }}
                showSearch
                optionFilterProp="children"
              >
                {docTemplates.map(t => (
                  <Option key={t.id} value={t.id}>{t.title}</Option>
                ))}
              </Select>
              <div className="mt-2 flex items-center gap-2">
                <input type="checkbox" id="scm_editDoc" checked={editDoc} onChange={e => {
                  setEditDoc(e.target.checked);
                  if (e.target.checked) {
                    const tpl = docTemplates.find(d => d.id === documentTemplateId);
                    setOverrideDocumentHtml(tpl?.body_html || '');
                  }
                }} />
                <label htmlFor="scm_editDoc">Customize before sending</label>
              </div>
              {editDoc && (
                <div className="mt-2">
                  <TextArea rows={6} value={overrideDocumentHtml} onChange={e => setOverrideDocumentHtml(e.target.value)} placeholder="HTML body (supports {{placeholders}})" />
                </div>
              )}
            </>
          )}
          {docMode === 'custom' && (
            <div className="mt-2">
              <TextArea rows={6} value={overrideDocumentHtml} onChange={e => setOverrideDocumentHtml(e.target.value)} placeholder="HTML body (supports {{placeholders}})" />
            </div>
          )}
        </div>

        {/* Email Section */}
        <div>
          <label className="block mb-2 font-medium">Email content</label>
          <div className="flex items-center gap-4 mb-3">
            <label className="flex items-center gap-2">
              <input type="radio" name="emailMode" value="template" checked={emailMode === 'template'} onChange={() => setEmailMode('template')} />
              <span>Use email template</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" name="emailMode" value="custom" checked={emailMode === 'custom'} onChange={() => setEmailMode('custom')} />
              <span>Type my own</span>
            </label>
          </div>

          {emailMode === 'template' && (
            <>
              <Select
                className="w-full"
                placeholder="Select email template"
                value={emailTemplateId}
                onChange={v => {
                  setEmailTemplateId(v);
                  if (editEmail) {
                    const tpl = emailTemplates.find(em => em.id === v);
                    setOverrideEmailSubject(tpl?.subject || '');
                    setOverrideEmailBody(tpl?.content || '');
                  }
                }}
                showSearch
                optionFilterProp="children"
              >
                {emailTemplates.map(t => (
                  <Option key={t.id} value={t.id}>{t.title}</Option>
                ))}
              </Select>
              <div className="mt-2 flex items-center gap-2">
                <input type="checkbox" id="scm_editEmail" checked={editEmail} onChange={e => {
                  setEditEmail(e.target.checked);
                  if (e.target.checked) {
                    const tpl = emailTemplates.find(em => em.id === emailTemplateId);
                    setOverrideEmailSubject(tpl?.subject || '');
                    setOverrideEmailBody(tpl?.content || '');
                  }
                }} />
                <label htmlFor="scm_editEmail">Customize before sending</label>
              </div>
              {editEmail && (
                <div className="mt-2 space-y-2">
                  <Input placeholder="Email subject" value={overrideEmailSubject} onChange={e => setOverrideEmailSubject(e.target.value)} />
                  <TextArea rows={6} placeholder="Email HTML body" value={overrideEmailBody} onChange={e => setOverrideEmailBody(e.target.value)} />
                </div>
              )}
            </>
          )}
          {emailMode === 'custom' && (
            <div className="mt-2 space-y-2">
              <Input placeholder="Email subject" value={overrideEmailSubject} onChange={e => setOverrideEmailSubject(e.target.value)} />
              <TextArea rows={6} placeholder="Email HTML body" value={overrideEmailBody} onChange={e => setOverrideEmailBody(e.target.value)} />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
